import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { PaperWrapper, Title } from '../common/styled';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import StaffAttendanceFilters from './components/StaffAttendanceFilters';
import StaffAttendanceTable from './components/StaffAttendanceTable';
import {
  StaffAttendance,
  StaffAttendanceRowData
} from './types/StaffAttendanceTypes';
import { transformStaffAttendanceReportData } from './utils/transformStaffAttendanceReportData';
import { Button, CircularProgress } from '@mui/material';
import { ButtonContainer } from '../Exams/styled';
import { fetchStaffAttendanceRange } from './utils/fetchStaffAttendanceRange';
import { downloadReport } from '../../utils/reportUtils';
import { getStaffAttendanceRoles } from '../Staff/config/constant';

const StaffAttendanceReportPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.profile);

  const [role, setRole] = useState<string>('All');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [rows, setRows] = useState<StaffAttendanceRowData[]>([]);
  const [attendanceData, setAttendanceData] = useState<StaffAttendance[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };
  const handleCloseToast = () => setToastOpen(false);

  const isFormIncomplete = !fromDate || !toDate;
  const roleOptions = [
    { label: 'All', value: 'All' },
    ...getStaffAttendanceRoles(user?.school)
  ];

  const fetchAttendanceData = async (successMessage: string) => {
    setSearching(true);
    try {
      const allData = await fetchStaffAttendanceRange({
        school: user?.school || '',
        fromDate,
        toDate,
        role: role === 'All' ? '' : role
      });
      setAttendanceData(allData);
      const data = transformStaffAttendanceReportData(
        allData
      ) as StaffAttendanceRowData[];
      setRows(data);
      showToast(successMessage, SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message || 'Error fetching staff attendance report',
        SnackbarSeverityType.error
      );
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = async () => {
    if (isFormIncomplete) return;
    setRows([]);
    setHasSearched(true);
    await fetchAttendanceData('Successfully fetched staff attendance report');
  };

  const handleDownload = () => {
    if (!attendanceData?.length) {
      showToast('No data available to download', SnackbarSeverityType.error);
      return;
    }

    const formatDateForFile = (date: string) => {
      if (!date) return '';
      const [yyyy, mm, dd] = date.split('-');
      return `${dd}-${mm}-${yyyy}`;
    };
    const from = formatDateForFile(fromDate);
    const to = formatDateForFile(toDate);
    let fileName = 'Staff_Attendance_Report';
    if (role !== 'All') fileName += `_${role}`;
    fileName += `_${from}-${to}`;

    const downloadData = transformStaffAttendanceReportData(
      attendanceData,
      true
    ) as Record<string, any>[];
    const filteredDownloadData = downloadData.map(({ id, ...rest }) => rest);
    downloadReport(
      filteredDownloadData,
      fileName,
      'Staff Attendance Report',
      showToast
    );
  };

  useEffect(() => {
    if (!role) return;
    setRows([]);
    setHasSearched(false);
    // eslint-disable-next-line
  }, [role]);

  return (
    <>
      <Title>Staff Attendance Report</Title>
      <PaperWrapper>
        <StaffAttendanceFilters
          role={role}
          setRole={setRole}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          setHasSearched={setHasSearched}
          roleOptions={roleOptions}
        />
        <ButtonContainer>
          <Button
            onClick={handleSearch}
            disabled={searching || isFormIncomplete}
            variant='contained'
          >
            {searching ? <CircularProgress size={24} /> : 'View Report'}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!rows?.length}
            variant='contained'
          >
            Download Report
          </Button>
        </ButtonContainer>
      </PaperWrapper>
      {hasSearched && <StaffAttendanceTable rows={rows} loading={searching} />}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default StaffAttendanceReportPage;
